import React, { useEffect, useState } from "react";
import Layout from "../../Components/Layout";
import styles from "./MyBusinessForSale.module.css";
import {
    getUserBusinessForSales,
    deleteUserBusinessForSale,
} from "../../Services/APIrequests";
import { useNavigate } from "react-router-dom";
import { RotatingLines } from "react-loader-spinner";
import { MdStorefront } from "react-icons/md";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import BusinessCard from "../../Components/LandingPage/BusinessesforSale/BusinessCard";
import EditBusinessModal from "./EditBusinessModal";
import Swal from "sweetalert2";
import { warningSwal } from "../../Util/Toast";

const MyBusinessForSale = () => {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingBusiness, setEditingBusiness] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const token = sessionStorage.getItem("token");
        if (!token) {
            navigate("/login", {
                state: { from: "/kohere/mybusinessforsale" },
            });
            return;
        }
        fetchMyBusinesses();
    }, []);

    const fetchMyBusinesses = async () => {
        try {
            setLoading(true);
            const res = await getUserBusinessForSales();
            const data = res.data?.userBusinessForSales || [];
            setBusinesses(data);
        } catch (err) {
            console.error("Error fetching user businesses for sale:", err);
            setError("Failed to load your businesses. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (business) => {
        const result = await Swal.fire({
            title: "Delete Listing?",
            html: `Are you sure you want to delete <strong>${business.business_name}</strong>?<br/><span style="font-size:13px;color:#ef4444;">This action cannot be undone.</span>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        try {
            await deleteUserBusinessForSale(business.CIN);
            Swal.fire({
                title: "Deleted!",
                text: "The business listing has been removed.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });
            setBusinesses((prev) => prev.filter((b) => b.CIN !== business.CIN));
        } catch (err) {
            console.error("Delete error:", err);
            warningSwal(
                err?.response?.data?.message ||
                "Failed to delete. Please try again."
            );
        }
    };

    return (
        <Layout Active={"My Business For Sale"}>
            <div className={styles.pageWrapper}>
                {/* Page Header */}
                <div className={styles.pageHeader}>
                    <div className={styles.headerIcon}>
                        <MdStorefront size={28} />
                    </div>
                    <div>
                        <h1 className={styles.pageTitle}>
                            My Business For Sale
                        </h1>
                        <p className={styles.pageSubtitle}>
                            Manage all the businesses you have listed for sale
                        </p>
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className={styles.loaderWrap}>
                        <RotatingLines
                            height="60"
                            width="60"
                            color="#4fa94d"
                            ariaLabel="loading"
                            visible={true}
                        />
                    </div>
                ) : error ? (
                    <div className={styles.errorWrap}>
                        <p className={styles.errorText}>{error}</p>
                        <button
                            className={styles.retryBtn}
                            onClick={fetchMyBusinesses}>
                            Retry
                        </button>
                    </div>
                ) : businesses.length === 0 ? (
                    <div className={styles.emptyWrap}>
                        <MdStorefront size={60} className={styles.emptyIcon} />
                        <h2 className={styles.emptyTitle}>
                            No Businesses Listed Yet
                        </h2>
                        <p className={styles.emptySubtitle}>
                            You haven't listed any businesses for sale. Go to
                            the Businesses for Sale section to create a listing.
                        </p>
                        <button
                            className={styles.browseBtn}
                            onClick={() => navigate("/BusinessesforSale")}>
                            Browse Businesses for Sale
                        </button>
                    </div>
                ) : (
                    <>
                        <div className={styles.resultsInfo}>
                            <span className={styles.resultsCount}>
                                {businesses.length} listing
                                {businesses.length !== 1 ? "s" : ""} found
                            </span>
                        </div>
                        <div className={styles.cardsGrid}>
                            {businesses.map((business, idx) => (
                                <div
                                    key={business.CIN || idx}
                                    className={styles.cardWrapper}>
                                    <BusinessCard
                                        CIN={business.CIN}
                                        title={business.business_name}
                                        location={business.location}
                                        about_company={business.about_company}
                                        pat={business.pat}
                                        turn_over={business.turn_over}
                                        isContact={business.isContact}
                                        paid_up_capital={business.paid_up_capital}
                                        authorised_capital={business.authorised_capital}
                                        sale_price={business.sale_price}
                                        business_image={business.business_image}
                                        date_of_incorporation={business.date_of_incorporation}
                                        legal_entity={business.legal_entity}
                                        industry={
                                            Array.isArray(business.industry)
                                                ? business.industry[0]
                                                : business.industry
                                        }
                                        hideContact={true}
                                    />
                                    {/* Action bar below the card */}
                                    <div className={styles.actionBar}>
                                        <button
                                            className={styles.editBtn}
                                            onClick={() =>
                                                setEditingBusiness(business)
                                            }
                                            title="Edit listing">
                                            <FiEdit2 size={14} />
                                            Edit
                                        </button>
                                        <button
                                            className={styles.deleteBtn}
                                            onClick={() =>
                                                handleDelete(business)
                                            }
                                            title="Delete listing">
                                            <FiTrash2 size={14} />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Edit Modal */}
            {editingBusiness && (
                <EditBusinessModal
                    business={editingBusiness}
                    onClose={() => setEditingBusiness(null)}
                    onUpdated={fetchMyBusinesses}
                />
            )}
        </Layout>
    );
};

export default MyBusinessForSale;
